/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { diff, flattenChangeset } from 'json-diff-ts';
import _ from 'lodash';
import prettyBytes from 'pretty-bytes';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, Checkbox, FormControlLabel, FormGroup, Tooltip } from '@mui/material';

import { PgConfig } from 'shared';

import { WarningIcon } from '../../../../../../../../components/svg-icons/WarningIcon';
import {
  CapacityUnits,
  UnitsToBytesMultiplier,
} from '../../../../../../../../elements/HUBytesInput/components/UnitPicker.types';
import { Namespaces } from '../../../../../../../../i18n';
import { useContextWarningsStyles } from '../../../../../ParametersTab/components/ContextWarning/ContextWarnings.styles';
import { useChangePlanParameterSuggestionsStyles } from './ChangePlanParameterSuggestions.styles';

type Props = {
  selectedSuggestions: Record<string, unknown>;
  setSelectedSuggestions: (value: Record<string, unknown>) => void;
  parameters: PgConfig.Config;
} & Record<'current' | 'next', Record<'cpu' | 'memory' | 'storage', number>>;

export function ChangePlanParameterSuggestions(props: Props) {
  const { t } = useTranslation(Namespaces.Cluster);

  const classes = useChangePlanParameterSuggestionsStyles();
  const warningClasses = useContextWarningsStyles({ indent: 0 });

  const { current, next, selectedSuggestions, setSelectedSuggestions } = props;

  const currentPgConfig = useMemo(
    () => PgConfig.use({ cluster: { cpu: current.cpu, memory: current.memory, storage: current.storage } }),
    [current.cpu, current.memory, current.storage],
  );
  const nextPgConfig = useMemo(
    () => PgConfig.use({ cluster: { cpu: next.cpu, memory: next.memory, storage: next.storage } }),
    [next.cpu, next.memory, next.storage],
  );

  // Reset the selection if any cluster resources get changed
  useEffect(() => {
    setSelectedSuggestions({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextPgConfig]);

  const suggestions = useMemo(() => {
    const currentDefaults = currentPgConfig.defaults();
    const nextDefaults = nextPgConfig.defaults();
    const normalized = nextPgConfig.normalize(props.parameters);

    let suggestions = flattenChangeset(diff(currentDefaults, nextDefaults));
    let outOfBounds = flattenChangeset(diff(props.parameters, normalized));

    // Overwrite the outOfBounds items with the default values
    outOfBounds = outOfBounds.map(item => ({
      ...item,
      value: suggestions.find(i => i.key === item.key)?.value ?? item.value,
    }));
    // Filter out duplicates from suggestions
    suggestions = suggestions.filter(item => !outOfBounds.find(i => i.key === item.key));

    return (
      [
        ...suggestions.map(item => ({ ...item, isOutOfBounds: false })),
        ...outOfBounds.map(item => ({ ...item, isOutOfBounds: true })),
      ]
        .map(item => {
          // Strips the leading `$.` so it may be used with lodash
          const path = item.path.slice(2);

          // Prefer settings present in the current PostgreSQL parameters of the cluster of default values
          const parameterValue = _.get(props.parameters, path);

          return {
            ...item,
            path,
            unit: PgConfig.getUiSchemaOption(item.key)?.unit,
            oldValue: parameterValue ?? item.oldValue,
          };
        })
        // Filter out suggestions that would not cause a change
        .filter(item => item.oldValue !== item.value)
        .sort((a, b) => a.key.localeCompare(b.key))
    );
  }, [currentPgConfig, nextPgConfig, props.parameters]);

  // Finds the longest value to have a uniform width
  const longestSuggestionKey = useMemo(
    () =>
      suggestions.reduce((max, cur) => {
        const length = cur.key.length + (cur.isOutOfBounds ? 2 : 0);
        return length > max ? length : max;
      }, 0),
    [suggestions],
  );

  const toggleItem = useCallback(
    (item: (typeof suggestions)[number]) =>
      setSelectedSuggestions({
        ...selectedSuggestions,
        ...(selectedSuggestions[item.path] ? { [item.path]: undefined } : { [item.path]: item.value }),
      }),
    [selectedSuggestions, setSelectedSuggestions],
  );

  return (
    <>
      <Box className={classes.sectionTitle}>
        <div className={classes.title}>{t('plan.pgConfig.title')}</div>
      </Box>
      <Box className={classes.sectionContainer}>
        <Box className={classes.suggestionsContainer}>
          <FormGroup>
            {suggestions.length ? (
              suggestions.map((item, i) => (
                <FormControlLabel
                  key={i}
                  control={
                    <Checkbox
                      checked={props.selectedSuggestions[item.path] !== undefined}
                      onChange={() => toggleItem(item)}
                      name={item.key}
                    />
                  }
                  className={classes.formControlLabel}
                  label={
                    <Box sx={{ fontFamily: 'Monospace', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      <Box
                        style={{
                          display: 'flex',
                          width: `${longestSuggestionKey + 1}ch`,
                          alignItems: 'center',
                          marginRight: 8,
                        }}
                      >
                        {item.isOutOfBounds ? (
                          <Tooltip title={t('plan.pgConfig.unchangedWarning')}>
                            <span className={warningClasses.warn}>
                              <WarningIcon className={warningClasses.warnIcon} />
                            </span>
                          </Tooltip>
                        ) : null}
                        {item.key}:
                      </Box>
                      <Box display="flex" alignItems="center">
                        <span>
                          {item.unit
                            ? prettyBytes(item.oldValue * UnitsToBytesMultiplier[item.unit as CapacityUnits])
                            : item.oldValue}
                        </span>
                        <KeyboardArrowRightIcon />
                        <span>
                          {item.unit
                            ? prettyBytes(item.value * UnitsToBytesMultiplier[item.unit as CapacityUnits])
                            : item.value}
                        </span>
                      </Box>
                    </Box>
                  }
                />
              ))
            ) : (
              <Box>{t('plan.pgConfig.noSuggestions')}</Box>
            )}
          </FormGroup>
        </Box>
      </Box>
    </>
  );
}
